import { logger } from 'cadenza/utils/logging';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import {
  calculateRectangleCenter,
  getParametersForDrawingTheImageOnTheCanvasContext,
  rotateCanvasContextIfItemToDrawIsRotated,
  unrotateCanvasContextToDrawItemUnrotated
} from 'cadenza/map/geom/canvas-renderer-utilities';

import type Feature from 'ol/Feature';
import type { Coordinate } from 'ol/coordinate';
import type { RenderFunction } from 'ol/style/Style';

/**
 * Creates the render function for an image object
 *
 * @param image
 * @param keepAspect - If false the aspect ratio is not kept
 */
export function getImageObjectRenderer (
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  keepAspect: boolean = true): RenderFunction {
  return (coords, state, ...args) => {
    const coordinatesAsArray = coords as Coordinate[][];
    if (!(coordinatesAsArray && coordinatesAsArray.length === 1 && coordinatesAsArray[0].length >= 4)) { // Because of a for me unknown reason there can be cases with 4 instead of 5 coordinates - Why? I don't know.
      logger.warn('For displaying an image a rectangle geometry is expected, '
          + 'which contains exactly one linear ring with at least 4 coordinates.', coordinatesAsArray);
      return;
    }

    const [ [ coordA, coordB, coordC, coordD ] ] = coordinatesAsArray; // The corners A, B, C and D of a (rotated or unrotated) rectangle
    const { context, feature } =
        state as { context: CanvasRenderingContext2D; feature: Feature };
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Rectangle)) {
      logger.error('trying to draw other geometry with image object renderer', coords, state, args);
      return;
    }

    const centerCoordinate = calculateRectangleCenter(coordA, coordB, coordC, coordD);
    const clockwiseRotationInDegree0To359 = geometry.getClockwiseRotationInDegree0To359();

    const {
      dx,
      dy,
      dWidth,
      dHeight
    } = getParametersForDrawingTheImageOnTheCanvasContext(coordA, coordC, centerCoordinate, clockwiseRotationInDegree0To359, image, keepAspect);

    // Draw the image now on the CanvasRenderingContext2D. For doing this:
    // 1.) Save the current state of the context to the stack
    context.save();
    // 2.) Reverse rotate the context (if needed) to be able to draw a rotated image with the same functionality as an unrotated image
    if (clockwiseRotationInDegree0To359 > 0) {
      unrotateCanvasContextToDrawItemUnrotated(context, centerCoordinate, clockwiseRotationInDegree0To359);
    }
    // 3. Draw the image in an unrotated state
    context.drawImage(image, dx, dy, dWidth, dHeight);
    // 4. Rotate the context to the initial state (if needed) after drawing the image
    if (clockwiseRotationInDegree0To359 > 0) {
      rotateCanvasContextIfItemToDrawIsRotated(context, centerCoordinate, clockwiseRotationInDegree0To359);
    }
    // 5.) Restore the previous state of the context again
    context.restore();
  };
}

